import { ReactNode, forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import classNames from 'classnames';
import { Typography } from '../typography/typography';

export type TavNabV2Ref = {
  setTab: (tab: string) => void;
} | null;

type TabNavV2 = {
  className?: string;
  tabNames: string[];
  onSelect: (tab: string) => ReactNode;
  useEcommerceTheme?: boolean;
};

export const TabNavV2 = forwardRef<TavNabV2Ref, TabNavV2>(
  ({ tabNames, onSelect, className, useEcommerceTheme = false }: TabNavV2, ref) => {
    const [selectedTabName, setSelectedTabName] = useState<string>();
    const [content, setContent] = useState<ReactNode>(null);

    function onTabChange(tab: string) {
      setSelectedTabName(tab);
      setContent(onSelect(tab));
    }

    // Set tab from the outside
    useImperativeHandle(
      ref,
      () => {
        return {
          setTab(tab: string) {
            onTabChange(tab);
          },
        };
      },
      [],
    );

    useEffect(() => {
      tabNames.length && onTabChange(tabNames[0]);
    }, []);

    return (
      <div className="m-0">
        <ul
          className={classNames(
            'bg-white min-w-full mx-0 gap-[24px] flex items-left list-none overflow-x-auto',
            className,
          )}
        >
          {tabNames.map((tabName: string, i) => (
            <li
              key={i}
              className={classNames('cursor-pointer py-2', {
                'border-solid border-0 border-b-2': tabName === selectedTabName,
                'border-[var(--primary-color-500)]': useEcommerceTheme && tabName === selectedTabName,
              })}
              onClick={() => onTabChange(tabName)}
            >
              <Typography
                className={classNames('whitespace-nowrap text-[var(--grey-500)]', {
                  '!text-[var(--quipli-black-800)]': !useEcommerceTheme && tabName === selectedTabName,
                  '!text-[var(--primary-color-500)]': useEcommerceTheme && tabName === selectedTabName,
                })}
                weight="semi-bold"
                type="text-sm"
              >
                {tabName}
              </Typography>
            </li>
          ))}
        </ul>
        <div className="m-0 p-0">{content}</div>
      </div>
    );
  },
);
